import { jsPDF } from "jspdf";
import config from "../../../config";
import { getAppliedDiscount } from "../../../utils/pricingTiers";
import { resolveLogoUrl } from "../../../utils/resolveLogoUrl";

// Helper function to load image as base64
const loadImageAsBase64 = (url) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      try {
        const dataURL = canvas.toDataURL("image/png");
        resolve(dataURL);
      } catch (e) {
        reject(e);
      }
    };
    img.onerror = () => reject(new Error("Failed to load image"));
    img.src = url;
  });
};

export const generateInvoicePDF = async (contract, accountSettings) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  let yPos = 15;

  // Professional color palette
  const colors = {
    primary: [31, 41, 55],        // Dark slate for headers
    accent: [59, 130, 246],       // Blue accent
    secondary: [107, 114, 128],   // Gray for labels
    text: [17, 24, 39],           // Near black for body text
    light: [156, 163, 175],       // Light gray
    line: [229, 231, 235],        // Very light gray for lines
    success: [16, 185, 129],      // Green for paid/discounts
    warning: [245, 158, 11],      // Orange for pending
    error: [239, 68, 68],         // Red for unpaid
    tableHeader: [243, 244, 246], // Light gray background
  };

  // Calculate discount from job override or global tiers
  const products = contract.products || [];
  const totalQuantity = products.reduce((sum, p) => sum + (parseInt(p.quantity) || 1), 0);

  // Get applied discount using utility function
  const appliedDiscount = getAppliedDiscount(contract, accountSettings, totalQuantity);
  const discountPercent = appliedDiscount.discount;
  const discountLabel = appliedDiscount.label;
  const hasDiscount = discountPercent > 0;
  const discountMultiplier = 1 - (discountPercent / 100);

  // Get tax rate from account settings
  const taxRate = accountSettings?.taxRate ? parseFloat(accountSettings.taxRate) / 100 : 0.06;

  // Helper function to add text
  const addText = (text, x, y, options = {}) => {
    doc.setFontSize(options.fontSize || 10);
    doc.setFont("helvetica", options.fontStyle || "normal");
    doc.text(text || "", x, y, options);
    return y + (options.lineHeight || 5);
  };

  // Helper function to draw a line
  const drawLine = (y, startX = margin, endX = pageWidth - margin) => {
    doc.setDrawColor(...colors.line);
    doc.setLineWidth(0.5);
    doc.line(startX, y, endX, y);
    return y + 3;
  };

  // Try to load and add company logo
  const logoUrl = contract.companyLogoUrl || accountSettings?.logoUrl;
  let logoLoaded = false;
  if (logoUrl) {
    try {
      const logoBase64 = await loadImageAsBase64(resolveLogoUrl(logoUrl));
      // Add logo at top left
      const logoHeight = 18;
      const logoWidth = 18;
      doc.addImage(logoBase64, "PNG", margin, yPos, logoWidth, logoHeight);
      logoLoaded = true;
    } catch (error) {
      console.warn("Could not load logo for PDF:", error);
    }
  }

  // Header - Company Name (positioned after logo or at top)
  const headerX = logoLoaded ? margin + 22 : margin;
  doc.setFontSize(20);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...colors.primary);
  doc.text(contract.companyName || accountSettings?.companyName || "Company Name", headerX, yPos + 8);

  // Company Contact Info (next to logo/name)
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...colors.secondary);
  const companyAddress = contract.companyAddress || accountSettings?.address || "";
  const companyPhone = contract.companyPhone || accountSettings?.phone || "";
  const companyEmail = contract.companyEmail || accountSettings?.email || "";

  let contactY = yPos + 13;
  if (companyAddress) {
    doc.text(companyAddress, headerX, contactY);
    contactY += 4;
  }
  if (companyPhone) {
    doc.text(`Phone: ${companyPhone}`, headerX, contactY);
    contactY += 4;
  }
  if (companyEmail) {
    doc.text(`Email: ${companyEmail}`, headerX, contactY);
    contactY += 4;
  }

  // Invoice Title and Number (right side)
  doc.setFontSize(28);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...colors.accent);
  doc.text("INVOICE", pageWidth - margin, yPos + 10, { align: "right" });

  // Invoice date and status
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...colors.secondary);
  doc.text(`Date: ${contract.date || contract.contractDate || new Date().toLocaleDateString()}`, pageWidth - margin, yPos + 18, { align: "right" });

  // Status badge
  const status = contract.status || "UNPAID";
  const statusColor = status === "PAID" ? colors.success : status === "PARTIAL" ? colors.warning : colors.error;
  doc.setTextColor(...statusColor);
  doc.setFont("helvetica", "bold");
  doc.text(status, pageWidth - margin, yPos + 24, { align: "right" });

  yPos = Math.max(contactY, yPos + 28) + 5;
  yPos = drawLine(yPos);
  yPos += 8;

  // Bill To Section
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...colors.primary);
  doc.text("BILL TO", margin, yPos);
  yPos += 6;

  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...colors.text);
  doc.text(contract.customerName || "Customer Name", margin, yPos);
  yPos += 5;

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...colors.secondary);
  if (contract.customerAddress) {
    const addressLines = doc.splitTextToSize(contract.customerAddress, 80);
    addressLines.forEach((line) => {
      doc.text(line, margin, yPos);
      yPos += 4;
    });
  }

  yPos += 8;

  // Products Table
  if (products.length > 0) {
    // Table header
    const tableStartY = yPos;
    doc.setFillColor(...colors.tableHeader);
    doc.rect(margin, yPos - 4, pageWidth - (margin * 2), 10, 'F');

    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...colors.primary);
    doc.text("PRODUCT", margin + 3, yPos + 2);
    doc.text("QTY", pageWidth - margin - 65, yPos + 2);
    doc.text("PRICE", pageWidth - margin - 40, yPos + 2);
    doc.text("TOTAL", pageWidth - margin - 3, yPos + 2, { align: "right" });
    yPos += 10;

    // Table rows
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...colors.text);
    let subtotal = 0;

    products.forEach((product, index) => {
      if (yPos > pageHeight - 60) {
        doc.addPage();
        yPos = 20;
      }

      const originalPrice = product.price || 0;
      const discountedPrice = hasDiscount ? originalPrice * discountMultiplier : originalPrice;
      const lineTotal = discountedPrice * (product.quantity || 1);
      subtotal += lineTotal;

      // Alternating row background
      if (index % 2 === 1) {
        doc.setFillColor(250, 250, 250);
        doc.rect(margin, yPos - 4, pageWidth - (margin * 2), 8, 'F');
      }

      doc.setFontSize(10);
      const productName = String(product.name || "Product").substring(0, 45);
      doc.text(productName, margin + 3, yPos);
      doc.text(String(product.quantity || 1), pageWidth - margin - 65, yPos);

      // Show discounted price
      if (hasDiscount) {
        doc.setTextColor(...colors.success);
        doc.text(`$${discountedPrice.toFixed(2)}`, pageWidth - margin - 40, yPos);
        doc.text(`$${lineTotal.toFixed(2)}`, pageWidth - margin - 3, yPos, { align: "right" });
        doc.setTextColor(...colors.text);
      } else {
        doc.text(`$${originalPrice.toFixed(2)}`, pageWidth - margin - 40, yPos);
        doc.text(`$${lineTotal.toFixed(2)}`, pageWidth - margin - 3, yPos, { align: "right" });
      }
      yPos += 8;
    });

    yPos += 3;
    drawLine(yPos, pageWidth - margin - 90, pageWidth - margin);
    yPos += 8;

    // Totals section (right aligned)
    const totalsX = pageWidth - margin - 90;

    // Calculate original subtotal for discount display
    const originalSubtotal = products.reduce((sum, p) => sum + (p.price || 0) * (p.quantity || 1), 0);

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...colors.secondary);
    doc.text("Subtotal:", totalsX, yPos);
    doc.setTextColor(...colors.text);
    doc.text(`$${originalSubtotal.toFixed(2)}`, pageWidth - margin - 3, yPos, { align: "right" });
    yPos += 6;

    // Show discount line if applicable
    if (hasDiscount) {
      const discountAmount = originalSubtotal - subtotal;
      doc.setTextColor(...colors.success);
      const discountLabelText = discountLabel
        ? `Discount (${discountPercent}% - ${discountLabel}):`
        : `Discount (${discountPercent}%):`;
      doc.text(discountLabelText, totalsX, yPos);
      doc.text(`-$${discountAmount.toFixed(2)}`, pageWidth - margin - 3, yPos, { align: "right" });
      yPos += 6;
    }

    if (contract.includeTax) {
      const tax = subtotal * taxRate;
      const taxPercent = (taxRate * 100).toFixed(0);
      doc.setTextColor(...colors.secondary);
      doc.text(`Tax (${taxPercent}%):`, totalsX, yPos);
      doc.setTextColor(...colors.text);
      doc.text(`$${tax.toFixed(2)}`, pageWidth - margin - 3, yPos, { align: "right" });
      yPos += 6;
    }

    // Total with emphasis
    const tax = contract.includeTax ? subtotal * taxRate : 0;
    const total = subtotal + tax;

    doc.setFillColor(...colors.primary);
    doc.rect(totalsX - 5, yPos - 4, pageWidth - margin - totalsX + 8, 10, 'F');
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(255, 255, 255);
    doc.text("TOTAL:", totalsX, yPos + 2);
    doc.text(`$${total.toFixed(2)}`, pageWidth - margin - 3, yPos + 2, { align: "right" });

    // Show savings message
    if (hasDiscount) {
      yPos += 12;
      doc.setFontSize(9);
      doc.setTextColor(...colors.success);
      const savings = originalSubtotal - subtotal;
      doc.text(`You saved $${savings.toFixed(2)}!`, pageWidth - margin - 3, yPos, { align: "right" });
    }

    yPos += 18;
  } else if (contract.totalPrice) {
    // Simple total display
    doc.setFillColor(...colors.primary);
    doc.rect(pageWidth - margin - 90, yPos - 4, 93, 14, 'F');
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(255, 255, 255);
    doc.text("TOTAL:", pageWidth - margin - 85, yPos + 4);
    doc.text(`$${parseFloat(contract.totalPrice || 0).toFixed(2)}`, pageWidth - margin - 3, yPos + 4, { align: "right" });
    yPos += 20;
  }

  // Payment Terms
  const paymentTermsLabel = contract.paymentTerms === "NET_15" ? "Net 15 Days"
    : contract.paymentTerms === "NET_30" ? "Net 30 Days"
    : contract.paymentTerms === "NET_60" ? "Net 60 Days"
    : "Due on Receipt";

  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...colors.primary);
  doc.text("Payment Terms:", margin, yPos);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...colors.text);
  doc.text(paymentTermsLabel, margin + 35, yPos);
  yPos += 10;

  // Payment Methods
  if (contract.paymentMethods) {
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...colors.primary);
    doc.text("Accepted Payment Methods:", margin, yPos);
    yPos += 5;

    doc.setFont("helvetica", "normal");
    doc.setTextColor(...colors.text);
    const paymentLines = doc.splitTextToSize(contract.paymentMethods, pageWidth - (margin * 2));
    paymentLines.forEach((line) => {
      doc.text(line, margin, yPos);
      yPos += 4;
    });
    yPos += 5;
  }

  // Notes Section
  if (contract.notes) {
    yPos += 5;
    doc.setFillColor(249, 250, 251);
    const notesLines = doc.splitTextToSize(contract.notes, pageWidth - (margin * 2) - 10);
    const notesHeight = Math.min(notesLines.length * 4 + 12, 50);
    doc.roundedRect(margin, yPos - 2, pageWidth - (margin * 2), notesHeight, 2, 2, 'F');

    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...colors.primary);
    doc.text("Notes:", margin + 5, yPos + 4);

    doc.setFont("helvetica", "normal");
    doc.setTextColor(...colors.secondary);
    let noteY = yPos + 10;
    notesLines.slice(0, 8).forEach((line) => {
      doc.text(line, margin + 5, noteY);
      noteY += 4;
    });
    yPos += notesHeight + 5;
  }

  // Footer
  yPos = pageHeight - 25;
  drawLine(yPos);
  yPos += 6;

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...colors.secondary);
  doc.text("Thank you for your business!", pageWidth / 2, yPos, { align: "center" });

  // Generate filename
  const customerName = (contract.customerName || "Customer").replace(/[^a-zA-Z0-9]/g, "_");
  const date = (contract.date || contract.contractDate || new Date().toISOString().split("T")[0]).replace(/[^a-zA-Z0-9]/g, "-");
  const filename = `Invoice_${customerName}_${date}.pdf`;

  // Save the PDF
  doc.save(filename);

  return filename;
};
